import { SKILL_LIBRARY_MAX, SKILL_SLOT_MAX } from "@/character/skills/constants";
import {
  isStorageQuotaError,
  loadGmShopCatalogFromIdb,
  saveGmShopCatalogToIdb,
} from "@/gm/gm-monster-catalog-idb";
import {
  catalogSlotBonus,
  type HomeEconomy,
  type HomePurchaseRecord,
} from "@/home/economy";
import { grantDungeonMonsterPowerCards } from "@/dungeon/dungeon-monster-power-card";
import {
  addHealthPotionsToWallet,
  HEALTH_POTION_HEAL_BY_TIER,
  HEALTH_POTION_LABEL_TH,
  tierFromFulfillment,
} from "@/home/health-potions";
import {
  addManaPotionsToWallet,
  tierFromManaFulfillment,
} from "@/home/mana-potions";
import { normalizePartKind, type HomePartKind } from "@/home/part-kind";
import {
  buildDefaultShopCatalogRows,
  CATALOG_SLOT_PACK_SIZE,
  SHOP_CURRENCY_LABELS,
  SHOP_FULFILLMENT_EDITOR_ORDER,
  SHOP_FULFILLMENT_KIND_SET,
  SHOP_FULFILLMENT_LABELS,
  shopPurchaseKindForFulfillment,
  shopSkuForFulfillment,
  type ShopCurrency,
  type ShopFulfillmentKind,
} from "@/home/shop-definitions";

export const GM_SHOP_CATALOG_VERSION = 1 as const;

export type GmShopCurrency = ShopCurrency;

export type GmShopFulfillmentKind = ShopFulfillmentKind;

export type GmShopItem = {
  version: typeof GM_SHOP_CATALOG_VERSION;
  id: string;
  name: string;
  description: string;
  imageDataUrl: string;
  currency: GmShopCurrency;
  price: number;
  /** 0–90 — ลดจากราคาปกติ */
  discountPercent: number;
  /** false = ไม่โชว์ในร้านผู้เล่น */
  saleEnabled: boolean;
  fulfillment: GmShopFulfillmentKind;
  catalogPartKind?: HomePartKind;
  catalogSlotsGranted?: number;
  sortOrder: number;
  createdAt: number;
  updatedAt: number;
};

export const GM_SHOP_PLACEHOLDER_IMAGE =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96"><rect fill="#1e293b" width="96" height="96" rx="12"/><text x="48" y="54" text-anchor="middle" fill="#94a3b8" font-family="system-ui,sans-serif" font-size="11">SHOP</text></svg>`,
  );

export const GM_SHOP_FULFILLMENT_LABELS = SHOP_FULFILLMENT_LABELS;

/** Dropdown in GM shop editor (legacy `health_potion` omitted — use size-specific rows). */
export const GM_SHOP_FULFILLMENT_EDITOR_ORDER = SHOP_FULFILLMENT_EDITOR_ORDER;

const LEGACY_HEALTH_POTION_ITEM_ID = "default-health-potion";

export const GM_SHOP_CURRENCY_LABELS = SHOP_CURRENCY_LABELS;

function newShopItemId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `gm-shop-${crypto.randomUUID()}`;
  }
  return `gm-shop-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function newPurchaseId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `purchase-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function clampInt(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(value)));
}

function clampPrice(value: unknown): number {
  return clampInt(value, 0, 99_999_999, 0);
}

const CURRENCY_SET = new Set<string>(["coins", "gems", "cash"]);

export function normalizeGmShopItem(raw: unknown): GmShopItem | null {
  if (!raw || typeof raw !== "object") return null;
  const p = raw as Partial<GmShopItem>;
  if (typeof p.id !== "string" || !p.id || typeof p.name !== "string" || !p.name.trim()) {
    return null;
  }
  const currency = typeof p.currency === "string" && CURRENCY_SET.has(p.currency)
    ? (p.currency as GmShopCurrency)
    : "coins";
  const fulfillment =
    typeof p.fulfillment === "string" && SHOP_FULFILLMENT_KIND_SET.has(p.fulfillment)
      ? (p.fulfillment as GmShopFulfillmentKind)
      : "health_potion_small";
  const now = Date.now();
  const kind =
    fulfillment === "catalog_slot_pack"
      ? normalizePartKind(p.catalogPartKind ?? "prop")
      : undefined;
  return {
    version: GM_SHOP_CATALOG_VERSION,
    id: p.id,
    name: p.name.trim().slice(0, 80),
    description: typeof p.description === "string" ? p.description.trim().slice(0, 400) : "",
    imageDataUrl:
      typeof p.imageDataUrl === "string" && p.imageDataUrl.startsWith("data:")
        ? p.imageDataUrl
        : GM_SHOP_PLACEHOLDER_IMAGE,
    currency,
    price: clampPrice(p.price),
    discountPercent: clampInt(p.discountPercent, 0, 90, 0),
    saleEnabled: p.saleEnabled !== false,
    fulfillment,
    catalogPartKind: kind,
    catalogSlotsGranted:
      fulfillment === "catalog_slot_pack"
        ? clampInt(p.catalogSlotsGranted, 1, 50, CATALOG_SLOT_PACK_SIZE)
        : undefined,
    sortOrder: clampInt(p.sortOrder, 0, 9999, 0),
    createdAt: typeof p.createdAt === "number" ? p.createdAt : now,
    updatedAt: typeof p.updatedAt === "number" ? p.updatedAt : now,
  };
}

export function gmShopEffectivePrice(item: GmShopItem): number {
  const discount = Math.min(90, Math.max(0, Math.floor(item.discountPercent)));
  return Math.max(0, Math.round((item.price * (100 - discount)) / 100));
}

export function gmShopListPrice(item: GmShopItem): number {
  return Math.max(0, Math.floor(item.price));
}

let catalogCache: GmShopItem[] | null = null;
let hydratePromise: Promise<void> | null = null;

function defaultCatalog(): GmShopItem[] {
  const now = Date.now();
  return buildDefaultShopCatalogRows().map((row) => ({
    version: GM_SHOP_CATALOG_VERSION,
    id: row.id,
    createdAt: now,
    updatedAt: now,
    name: row.name,
    description: row.description,
    imageDataUrl: GM_SHOP_PLACEHOLDER_IMAGE,
    currency: row.currency,
    price: row.price,
    discountPercent: 0,
    saleEnabled: true,
    fulfillment: row.fulfillment,
    catalogPartKind: row.catalogPartKind,
    catalogSlotsGranted: row.catalogSlotsGranted,
    sortOrder: row.sortOrder,
  }));
}

async function persistCatalog(items: GmShopItem[]): Promise<void> {
  const sorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  await saveGmShopCatalogToIdb(sorted);
  catalogCache = sorted;
}

function normalizeLoadedShopItem(entry: unknown): GmShopItem | null {
  const item = normalizeGmShopItem(entry);
  if (!item) return null;
  if (item.fulfillment === "health_potion") {
    return { ...item, fulfillment: "health_potion_small" };
  }
  return item;
}

function mergeShopCatalogWithDefaults(items: GmShopItem[]): GmShopItem[] {
  const defaults = defaultCatalog();
  const defaultById = new Map(defaults.map((entry) => [entry.id, entry]));
  let list = [...items];

  const legacy = list.find((entry) => entry.id === LEGACY_HEALTH_POTION_ITEM_ID);
  list = list.filter((entry) => entry.id !== LEGACY_HEALTH_POTION_ITEM_ID);
  if (legacy && !list.some((entry) => entry.id === "default-health-potion-small")) {
    const smallTemplate = defaultById.get("default-health-potion-small");
    list.push({
      ...legacy,
      id: "default-health-potion-small",
      name: smallTemplate?.name ?? HEALTH_POTION_LABEL_TH.small,
      description: smallTemplate?.description ?? `ฟื้น +${HEALTH_POTION_HEAL_BY_TIER.small} HP`,
      fulfillment: "health_potion_small",
      sortOrder: legacy.sortOrder || smallTemplate?.sortOrder || 10,
    });
  }

  for (const template of defaults) {
    if (!list.some((entry) => entry.id === template.id)) {
      list.push({ ...template });
    }
  }

  for (const fulfillment of SHOP_FULFILLMENT_EDITOR_ORDER) {
    if (list.some((entry) => entry.fulfillment === fulfillment)) continue;
    const template = defaults.find((entry) => entry.fulfillment === fulfillment);
    if (template) list.push({ ...template });
  }

  list = list.map((entry) => {
    const template = defaultById.get(entry.id);
    if (!template) return entry;
    return {
      ...entry,
      price: template.price,
      name: template.name,
      description: template.description,
      currency: template.currency,
      fulfillment: template.fulfillment,
    };
  });

  return list;
}

function catalogItemIdsSignature(items: GmShopItem[]): string {
  return items
    .map((entry) => entry.id)
    .sort()
    .join("|");
}

export async function hydrateGmShopCatalog(): Promise<void> {
  if (catalogCache) return;
  if (hydratePromise) {
    await hydratePromise;
    return;
  }
  hydratePromise = (async () => {
    const raw = await loadGmShopCatalogFromIdb();
    if (!raw || raw.length === 0) {
      catalogCache = defaultCatalog();
      try {
        await persistCatalog(catalogCache);
      } catch {
        /* keep in-memory */
      }
      return;
    }
    const loaded = raw
      .map(normalizeLoadedShopItem)
      .filter((item): item is GmShopItem => item !== null);
    const beforeIds = catalogItemIdsSignature(loaded);
    const merged = mergeShopCatalogWithDefaults(loaded);
    catalogCache = merged.length === 0 ? defaultCatalog() : merged;
    const shouldPersist =
      catalogItemIdsSignature(catalogCache) !== beforeIds ||
      JSON.stringify(catalogCache) !== JSON.stringify(loaded);
    if (shouldPersist) {
      try {
        await persistCatalog(catalogCache);
      } catch {
        /* keep in-memory */
      }
    }
  })();
  await hydratePromise;
}

export function loadGmShopCatalog(): GmShopItem[] {
  return catalogCache ? [...catalogCache] : [];
}

export function listGmShopItemsForPlayers(currency?: GmShopCurrency): GmShopItem[] {
  return loadGmShopCatalog()
    .filter((item) => item.saleEnabled)
    .filter((item) => (currency ? item.currency === currency : true))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

export function getGmShopItemById(id: string): GmShopItem | undefined {
  return loadGmShopCatalog().find((item) => item.id === id);
}

export type CreateGmShopItemInput = Omit<
  GmShopItem,
  "version" | "id" | "createdAt" | "updatedAt"
> & { id?: string };

export function createGmShopItemTemplate(input: CreateGmShopItemInput): GmShopItem {
  const now = Date.now();
  return normalizeGmShopItem({
    ...input,
    version: GM_SHOP_CATALOG_VERSION,
    id: input.id ?? newShopItemId(),
    createdAt: now,
    updatedAt: now,
  })!;
}

export async function upsertGmShopCatalog(item: GmShopItem): Promise<void> {
  const list = loadGmShopCatalog();
  const idx = list.findIndex((e) => e.id === item.id);
  const next = { ...item, updatedAt: Date.now() };
  if (idx >= 0) list[idx] = { ...list[idx], ...next };
  else list.push(next);
  await persistCatalog(list);
}

export async function deleteGmShopFromCatalog(id: string): Promise<void> {
  await persistCatalog(loadGmShopCatalog().filter((item) => item.id !== id));
}

export type PurchaseGmShopResult =
  | { ok: true; economy: HomeEconomy; item: GmShopItem; paid: number; quantity?: number }
  | { ok: false; reason: string };

export const GM_SHOP_MAX_BUY_QUANTITY = 99;

function walletCanPay(economy: HomeEconomy, currency: GmShopCurrency, amount: number): boolean {
  if (amount <= 0) return true;
  if (currency === "coins") return economy.wallet.coins >= amount;
  if (currency === "gems") return economy.wallet.gems >= amount;
  return economy.wallet.cash >= amount;
}

function deductWallet(
  economy: HomeEconomy,
  currency: GmShopCurrency,
  amount: number,
): HomeEconomy["wallet"] {
  const wallet = { ...economy.wallet };
  if (currency === "coins") wallet.coins -= amount;
  else if (currency === "gems") wallet.gems -= amount;
  else wallet.cash -= amount;
  return wallet;
}

export function purchaseGmShopItem(
  economy: HomeEconomy,
  item: GmShopItem,
  options?: { catalogPartKind?: HomePartKind },
): PurchaseGmShopResult {
  if (!item.saleEnabled) {
    return { ok: false, reason: "สินค้านี้ปิดการขาย" };
  }
  const paid = gmShopEffectivePrice(item);
  if (!walletCanPay(economy, item.currency, paid)) {
    const label = GM_SHOP_CURRENCY_LABELS[item.currency];
    return { ok: false, reason: `${label}ไม่พอ · ต้องการ ${paid.toLocaleString("en-US")}` };
  }

  let nextEconomy: HomeEconomy = {
    ...economy,
    wallet: deductWallet(economy, item.currency, paid),
  };

  const potionTier = tierFromFulfillment(item.fulfillment);
  if (potionTier) {
    nextEconomy = {
      ...nextEconomy,
      wallet: addHealthPotionsToWallet(nextEconomy.wallet, potionTier, 1),
    };
  } else {
    const manaTier = tierFromManaFulfillment(item.fulfillment);
    if (manaTier) {
      nextEconomy = {
        ...nextEconomy,
        wallet: addManaPotionsToWallet(nextEconomy.wallet, manaTier, 1),
      };
    }
  }
  if (!potionTier && !tierFromManaFulfillment(item.fulfillment)) switch (item.fulfillment) {
    case "revive_card":
      nextEconomy = {
        ...nextEconomy,
        wallet: {
          ...nextEconomy.wallet,
          reviveCards: nextEconomy.wallet.reviveCards + 1,
        },
      };
      break;
    case "skill_cooldown_card":
      nextEconomy = {
        ...nextEconomy,
        skillCooldownCards: nextEconomy.skillCooldownCards + 1,
      };
      break;
    case "skill_ap_card":
      nextEconomy = {
        ...nextEconomy,
        skillApCards: nextEconomy.skillApCards + 1,
      };
      break;
    case "skill_buff_duration_card":
      nextEconomy = {
        ...nextEconomy,
        skillBuffDurationCards: nextEconomy.skillBuffDurationCards + 1,
      };
      break;
    case "skill_aoe_range_card":
      nextEconomy = {
        ...nextEconomy,
        skillAoeRangeCards: nextEconomy.skillAoeRangeCards + 1,
      };
      break;
    case "skill_slot_card":
      if (nextEconomy.skillSlotsUnlocked >= SKILL_SLOT_MAX) {
        return { ok: false, reason: "ช่องสกิลเต็มแล้ว (8/8)" };
      }
      nextEconomy = {
        ...nextEconomy,
        skillSlotsUnlocked: Math.min(SKILL_SLOT_MAX, nextEconomy.skillSlotsUnlocked + 1),
      };
      break;
    case "skill_library_slot_card":
      if (nextEconomy.skillLibrarySlotsUnlocked >= SKILL_LIBRARY_MAX) {
        return {
          ok: false,
          reason: `คลังสกิลเต็มแล้ว (${SKILL_LIBRARY_MAX}/${SKILL_LIBRARY_MAX})`,
        };
      }
      nextEconomy = {
        ...nextEconomy,
        skillLibrarySlotsUnlocked: Math.min(
          SKILL_LIBRARY_MAX,
          nextEconomy.skillLibrarySlotsUnlocked + 1,
        ),
      };
      break;
    case "skill_create_card":
      nextEconomy = {
        ...nextEconomy,
        skillCreateCards: nextEconomy.skillCreateCards + 1,
      };
      break;
    case "shadow_create_card":
      nextEconomy = {
        ...nextEconomy,
        shadowCreateCards: nextEconomy.shadowCreateCards + 1,
      };
      break;
    case "shadow_ap_card":
      nextEconomy = {
        ...nextEconomy,
        shadowApCards: nextEconomy.shadowApCards + 1,
      };
      break;
    case "pet_create_card":
      nextEconomy = {
        ...nextEconomy,
        petCreateCards: nextEconomy.petCreateCards + 1,
      };
      break;
    case "pet_ap_card":
      nextEconomy = {
        ...nextEconomy,
        petApCards: nextEconomy.petApCards + 1,
      };
      break;
    case "pet_size_card":
      nextEconomy = {
        ...nextEconomy,
        petSizeCards: nextEconomy.petSizeCards + 1,
      };
      break;
    case "pet_skill_create_card":
      nextEconomy = {
        ...nextEconomy,
        petSkillCreateCards: nextEconomy.petSkillCreateCards + 1,
      };
      break;
    case "pet_skill_cooldown_card":
      nextEconomy = {
        ...nextEconomy,
        petSkillCooldownCards: nextEconomy.petSkillCooldownCards + 1,
      };
      break;
    case "pet_skill_ap_card":
      nextEconomy = {
        ...nextEconomy,
        petSkillApCards: nextEconomy.petSkillApCards + 1,
      };
      break;
    case "pet_skill_buff_duration_card":
      nextEconomy = {
        ...nextEconomy,
        petSkillBuffDurationCards: nextEconomy.petSkillBuffDurationCards + 1,
      };
      break;
    case "pet_skill_aoe_range_card":
      nextEconomy = {
        ...nextEconomy,
        petSkillAoeRangeCards: nextEconomy.petSkillAoeRangeCards + 1,
      };
      break;
    case "dungeon_floor_slot":
      break;
    case "dungeon_monster_power_card":
      nextEconomy = grantDungeonMonsterPowerCards(nextEconomy, 1);
      break;
    case "home_structure_hp_card":
      nextEconomy = {
        ...nextEconomy,
        homeStructureHpCards: (nextEconomy.homeStructureHpCards ?? 0) + 1,
      };
      break;
    case "home_structure_def_card":
      nextEconomy = {
        ...nextEconomy,
        homeStructureDefCards: (nextEconomy.homeStructureDefCards ?? 0) + 1,
      };
      break;
    case "home_structure_regen_card":
      nextEconomy = {
        ...nextEconomy,
        homeStructureRegenCards: (nextEconomy.homeStructureRegenCards ?? 0) + 1,
      };
      break;
    case "home_structure_hp_potion":
      nextEconomy = {
        ...nextEconomy,
        wallet: {
          ...nextEconomy.wallet,
          homeStructureHpPotions: (nextEconomy.wallet.homeStructureHpPotions ?? 0) + 1,
        },
      };
      break;
    case "catalog_slot_pack": {
      const kind = normalizePartKind(
        options?.catalogPartKind ?? item.catalogPartKind ?? "prop",
      );
      const granted = item.catalogSlotsGranted ?? CATALOG_SLOT_PACK_SIZE;
      nextEconomy = {
        ...nextEconomy,
        slotBonus: {
          ...nextEconomy.slotBonus,
          [kind]: catalogSlotBonus(nextEconomy, kind) + granted,
        },
      };
      break;
    }
    default:
      return { ok: false, reason: "ประเภทสินค้าไม่รองรับ" };
  }

  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: shopSkuForFulfillment(item.fulfillment),
    kind: shopPurchaseKindForFulfillment(item.fulfillment),
    status: "mock_granted",
    createdAt: Date.now(),
    provider: "gm_shop",
    partKind:
      item.fulfillment === "catalog_slot_pack"
        ? normalizePartKind(options?.catalogPartKind ?? item.catalogPartKind ?? "prop")
        : undefined,
    slotsGranted:
      item.fulfillment === "catalog_slot_pack"
        ? item.catalogSlotsGranted ?? CATALOG_SLOT_PACK_SIZE
        : item.fulfillment === "skill_slot_card" ||
            item.fulfillment === "skill_library_slot_card"
          ? 1
          : undefined,
    gemSpent: item.currency === "gems" ? paid : undefined,
    cashSpent: item.currency === "cash" ? paid : undefined,
  };

  nextEconomy = {
    ...nextEconomy,
    purchases: [record, ...nextEconomy.purchases].slice(0, 50),
  };

  return { ok: true, economy: nextEconomy, item, paid };
}

export function maxAffordableShopQuantity(economy: HomeEconomy, item: GmShopItem): number {
  const unit = gmShopEffectivePrice(item);
  if (unit <= 0) return GM_SHOP_MAX_BUY_QUANTITY;
  let balance = 0;
  if (item.currency === "coins") balance = economy.wallet.coins;
  else if (item.currency === "gems") balance = economy.wallet.gems;
  else balance = economy.wallet.cash;
  return Math.min(GM_SHOP_MAX_BUY_QUANTITY, Math.floor(balance / unit));
}

export function purchaseGmShopItemQuantity(
  economy: HomeEconomy,
  item: GmShopItem,
  quantity: number,
  options?: { catalogPartKind?: HomePartKind },
): PurchaseGmShopResult {
  const qty = Math.max(1, Math.min(GM_SHOP_MAX_BUY_QUANTITY, Math.floor(quantity)));
  let eco = economy;
  let totalPaid = 0;
  for (let i = 0; i < qty; i += 1) {
    const result = purchaseGmShopItem(eco, item, options);
    if (!result.ok) {
      if (i === 0) return result;
      return {
        ok: false,
        reason: `${result.reason} · ซื้อสำเร็จ ${i}/${qty} ชิ้น`,
      };
    }
    eco = result.economy;
    totalPaid += result.paid;
  }
  return { ok: true, economy: eco, item, paid: totalPaid, quantity: qty };
}

export { isStorageQuotaError };
